// redux/slices/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ActionTypesUser } from "../constants/action-types";
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { externalEventService } from '../../services/externalEventService';

const REACT_APP_CHATCON_URL = process.env.REACT_APP_CHATCON_URL;
const REACT_APP_TWILIO_API_URL = (process.env.REACT_APP_TWILIO_API_URL || '').trim();

interface UserState {
    user: any;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: UserState = {
    user: null,
    status: 'idle',
    error: null,
};

// Async thunk for Avaya sign-in (legacy)
export const signIn = createAsyncThunk(
    ActionTypesUser.USER_SIGN_IN_REQUEST,
    async ({ signInData, engagementParams }: { signInData: any; engagementParams: any }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post(`${REACT_APP_CHATCON_URL}/createSession`, signInData);
            const userData = { ...data, providerSenderId: uuidv4(), providerDialogId: uuidv4(), engagementParameters: engagementParams };
            return { userData };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to sign in');
        }
    }
);

// Async thunk for Twilio pre-engagement -> token + conversationSid
export const signInTwilio = createAsyncThunk(
    'USER_TWILIO_SIGN_IN_REQUEST',
    async ({ formData }: { formData: any }, { rejectWithValue }) => {
        try {
            console.log('🚀 ENGAGEMENT CREATION STARTED:', {
                endpoint: '/initWebchat',
                formData: formData,
                timestamp: new Date().toISOString()
            });

            const baseUrl = REACT_APP_TWILIO_API_URL || REACT_APP_CHATCON_URL || 'http://localhost:3010';
            const fullUrl = `${baseUrl}/initWebchat`;

            console.log('🌐 MAKING REQUEST TO:', {
                baseUrl,
                fullUrl,
                REACT_APP_TWILIO_API_URL,
                REACT_APP_CHATCON_URL,
                formData
            });

            const { data } = await axios.post(fullUrl, { formData });

            console.log('✅ ENGAGEMENT CREATION SUCCESS:', {
                token: data.token ? '***TOKEN_RECEIVED***' : 'NO_TOKEN',
                conversationSid: data.conversationSid,
                expiration: data.expiration,
                identity: data.identity,
                timestamp: new Date().toISOString()
            });

            // Send engagement creation event to external API
            try {
                const engagementEventData = {
                    conversationSid: data.conversationSid,
                    identity: data.identity,
                    token: data.token ? '***TOKEN_RECEIVED***' : 'NO_TOKEN',
                    expiration: data.expiration,
                    formData: formData,
                    timestamp: new Date().toISOString(),
                    action: 'created'
                };

                await externalEventService.processEvent('ENGAGEMENT_CREATED', engagementEventData, data.conversationSid);
            } catch (error) {
                console.error('❌ FAILED TO SEND ENGAGEMENT CREATED EVENT TO EXTERNAL API:', error);
            }

            // Normalize user object to reuse existing user slice
            const userData = {
                token: data.token,
                conversationSid: data.conversationSid,
                expiration: data.expiration,
                bbc_id: data.bbc_id,
                arion_route: data.arion_route,
                // Keep engagement parameters handy for UI/analytics
                engagementParameters: formData
            };
            return { userData };
        } catch (error: any) {
            console.error('❌ ENGAGEMENT CREATION FAILED:', {
                error: error.message,
                formData: formData,
                timestamp: new Date().toISOString()
            });
            return rejectWithValue(error?.response || error.message || 'Failed to initialize Twilio session');
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        restoreSession(state, action: PayloadAction<any>) {
            state.user = action.payload;
            state.status = 'succeeded';
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Avaya legacy
            .addCase(signIn.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(signIn.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.user = action.payload.userData;
                state.error = null;
            })
            .addCase(signIn.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            // Twilio path
            .addCase(signInTwilio.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(signInTwilio.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.user = action.payload.userData;
                state.error = null;
            })
            .addCase(signInTwilio.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });
    },
});

export const { restoreSession } = userSlice.actions;

export const selectUser = (state: { user: UserState }) => state.user.user;
export const selectUserStatus = (state: { user: UserState }) => state.user.status;
export const selectUserError = (state: { user: UserState }) => state.user.error;

export default userSlice.reducer;
